import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Compact link + outlined buttons (e.g. payments status dropdown).
 * Not the shared modal footer — use only where the compact dropdown look is required.
 */
const COMPACT_FOOTER_CLASSES = new Set(['modal-footer--compact']);

@Component({
  selector: 'app-modal-footer-actions',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modal-footer-actions.component.html',
  styleUrls: ['./modal-footer-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalFooterActionsComponent {
  /**
   * Class on the inner footer container. Default is the shared `.modal-footer` design
   * (source of truth). Use `modal-footer--compact` only for compact dropdown panels.
   */
  @Input() footerClass = 'modal-footer';

  @Input() secondaryText = 'Clear';
  @Input() cancelText = 'Cancel';
  @Input() primaryText = 'Apply';
  @Input() secondaryDisabled = false;
  @Input() cancelDisabled = false;
  @Input() primaryDisabled = false;
  @Input() primaryLoading = false;
  @Input() primaryLoadingText = 'Loading...';
  @Input() cancelDesktopOnly = false;
  @Input() secondaryDesktopOnly = false;
  @Input() showSecondary = true;
  @Input() showCancel = true;
  @Input() showPrimary = true;
  /**
   * When true, Cancel/Primary stack full-width on small screens (column-reverse).
   * Opt-in so other modals keep their current mobile layout.
   */
  @Input() stackOnMobile = false;
  /** Use `submit` when the footer sits inside a form that handles save via `(ngSubmit)`. */
  @Input() primaryButtonType: 'button' | 'submit' = 'button';
  @Input() primaryExtraClass: Record<string, boolean> | null = null;

  @Output() secondaryClick = new EventEmitter<void>();
  @Output() cancelClick = new EventEmitter<void>();
  @Output() primaryClick = new EventEmitter<void>();

  /** Compact dropdown panels (`modal-footer--compact`) keep link + outlined buttons. */
  get useCompactStyle(): boolean {
    return this.footerClass.split(/\s+/).some(cls => COMPACT_FOOTER_CLASSES.has(cls));
  }

  onPrimaryClick(): void {
    // `submit` is handled by the parent form's `(ngSubmit)`; avoid double-firing.
    if (this.primaryButtonType === 'button') {
      this.primaryClick.emit();
    }
  }
}
